import type { SVGProps } from "react";

/**
 * A tiny, dependency-free icon set for the shell nav (and other chrome). Lucide-style 24×24
 * stroke glyphs inlined as SVG so the staff SPA needs no icon runtime dependency and stays inside
 * its declared package boundary (dependency-cruiser). `currentColor` + `1em` size means each glyph
 * inherits the surrounding text colour and font-size, so nav active/hover states style it for free.
 */
export type IconName =
  | "overview"
  | "bookings"
  | "live"
  | "waitlist"
  | "onboarding"
  | "settings"
  | "schedule"
  | "floorPlan"
  | "media"
  | "guests"
  | "blacklist"
  | "abuseBlocks"
  | "site"
  | "billing"
  | "embed"
  | "metaReserve"
  | "arrivals"
  | "requests"
  | "covers"
  | "analytics"
  | "channels"
  | "staff"
  | "account"
  | "lock"
  | "menu"
  | "close";

const PATHS: Record<IconName, string> = {
  // home
  overview: "M3 10.5 12 3l9 7.5M5.25 9.5V20a1 1 0 0 0 1 1h11.5a1 1 0 0 0 1-1V9.5",
  // calendar
  bookings:
    "M7 3v4M17 3v4M4 8h16M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z",
  // activity pulse
  live: "M3 12h4l3 8 4-16 3 8h4",
  // list
  waitlist: "M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01",
  // rocket
  onboarding: "M12 3c3 1 6 4 6 9l-3 3H9l-3-3c0-5 3-8 6-9ZM9 15l-2 4M15 15l2 4M12 9h.01",
  // gear
  settings:
    "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM19 12a7 7 0 0 0-.1-1.3l2-1.5-2-3.4-2.3 1a7 7 0 0 0-2.3-1.3L13.8 2h-3.6l-.4 2.5a7 7 0 0 0-2.3 1.3l-2.3-1-2 3.4 2 1.5A7 7 0 0 0 5 12a7 7 0 0 0 .1 1.3l-2 1.5 2 3.4 2.3-1a7 7 0 0 0 2.3 1.3l.4 2.5h3.6l.4-2.5a7 7 0 0 0 2.3-1.3l2.3 1 2-3.4-2-1.5A7 7 0 0 0 19 12Z",
  // clock
  schedule: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 7v5l3 2",
  // layout grid
  floorPlan: "M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z",
  // image
  media: "M4 5h16v14H4zM4 15l4-4 5 5M14 14l2-2 4 4M9 9.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z",
  // users
  guests:
    "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM2.5 20a6.5 6.5 0 0 1 13 0M16 3.5a4 4 0 0 1 0 7.5M18 14a6.5 6.5 0 0 1 3.5 6",
  // shield-off
  blacklist: "M12 3l7 3v5c0 5-3.5 8-7 10-3.5-2-7-5-7-10V6l7-3ZM8.5 8.5l7 7",
  // shield
  abuseBlocks: "M12 3l7 3v5c0 5-3.5 8-7 10-3.5-2-7-5-7-10V6l7-3ZM9.5 12l1.8 1.8 3.5-3.6",
  // globe
  site: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM3 12h18M12 3c2.5 2.4 3.8 5.6 3.8 9S14.5 18.6 12 21c-2.5-2.4-3.8-5.6-3.8-9S9.5 5.4 12 3Z",
  // credit card
  billing: "M3 6h18a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1ZM2 10h20M6 15h4",
  // code
  embed: "M9 8l-4 4 4 4M15 8l4 4-4 4",
  // share-2 (social entry point)
  metaReserve:
    "M18 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM6 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM18 22a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM8.6 13.5l6.8 4M15.4 6.5l-6.8 4",
  // plane arrival
  arrivals: "M4 20h16M3 12l7 1 4-7 2 .5-2 4 4 1 2-2 1.5.4L18 14 4 12v-1.5Z",
  // inbox
  requests: "M4 13h4l1.5 3h5L16 13h4M4 13l2.5-8h11L20 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-6Z",
  // users-round (covers)
  covers: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM5 20a7 7 0 0 1 14 0",
  // bar chart (cross-venue analytics)
  analytics: "M4 20V10M10 20V4M16 20v-7M22 20H2",
  // broadcast / radio waves (booking distribution channels)
  channels:
    "M4.9 4.9a10 10 0 0 0 0 14.2M19.1 4.9a10 10 0 0 1 0 14.2M7.8 7.8a6 6 0 0 0 0 8.4M16.2 7.8a6 6 0 0 1 0 8.4M12 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z",
  // id badge (staff & roles)
  staff:
    "M9 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1h3a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h4ZM12 12a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM8.5 17a3.5 3.5 0 0 1 7 0",
  // user circle (my account)
  account:
    "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM6.5 18.4a5.5 5.5 0 0 1 11 0",
  // padlock (locked/plan-gated feature)
  lock: "M6 10V8a6 6 0 1 1 12 0v2M5 10h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1ZM12 14v3",
  // hamburger (open the mobile nav drawer)
  menu: "M4 6h16M4 12h16M4 18h16",
  // x (close the mobile nav drawer)
  close: "M6 6l12 12M18 6 6 18",
};

export function ShellIcon({ name, ...props }: { name: IconName } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
